import { ViewModule, ViewStream } from '@mui/icons-material';
import {
  ButtonGroup,
  FormControl,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
  SelectProps,
  TextFieldProps,
} from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import {
  StyledSelect,
  StyledSortView,
  StyledTextField,
  StyledViewButton,
} from './sortView.style';
import { handleKeyPress } from 'utils/functions';

interface Sort {
  name: string;
  value: string;
  label: string;
}

enum ViewMode {
  Mode1 = 1,
  Mode2,
}

type ViewData = Array<{ icon: JSX.Element; value: ViewMode }>;

export interface SortViewProps {
  sortData: Sort[];
  viewData?: ViewData;
  defaultSort?: Sort;
  labels?: Record<string, string>;
  showView?: boolean;
  onSearchChange?: (value: string) => void;
  onSortChange?: (value: string) => void;
  onViewChange?: (value: ViewMode) => void;
  showSort?: boolean;
  showSearch?: boolean;
  searchProps?: TextFieldProps;
  sortProps?: SelectProps;
  locale?: any;
}

const defaultViewData: ViewData = [
  {
    icon: <ViewModule />,
    value: ViewMode.Mode1,
  },
  {
    icon: <ViewStream />,
    value: ViewMode.Mode2,
  },
];

const SortView = (props: SortViewProps) => {
  const {
    sortData,
    viewData = defaultViewData,
    defaultSort,
    labels = {},
    showView = true,
    onSearchChange,
    onSortChange,
    onViewChange,
    showSort = true,
    showSearch = false,
    searchProps,
    sortProps,
    locale,
  } = props;

  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState(defaultSort?.value ?? sortData?.[0].value ?? '');
  const [viewValue, setViewValue] = useState<ViewMode>(ViewMode.Mode1);
  const [isClickedViewingButton, setIsClickedViewingButton] = useState(false);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const handleSortChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setSortValue(value);
    onSortChange?.(value);
  };

  const handleViewChange = (value: ViewMode) => {
    setIsClickedViewingButton(true);
    if (value === viewValue) return;
    setViewValue(value);
    onViewChange?.(value);
  };

  return (
    <StyledSortView>
      {showSearch && (
        <StyledTextField
          label={labels.search}
          value={searchValue}
          onChange={handleSearchChange}
          {...searchProps}
        />
      )}

      {showSort && (
        <FormControl variant="outlined">
          {labels.sort && <InputLabel htmlFor={'sortview-sort'}>{labels.sort}</InputLabel>}
          <StyledSelect
            label={labels.sort}
            value={sortValue}
            onChange={handleSortChange}
            {...sortProps}
            id='drp-sortview-sort'
            sx={{ minWidth: 140, ...sortProps?.sx }}
            inputProps={{id: 'sortview-sort', "aria-hidden": false, "tabindex": 0, "aria-labelledby": "sortview-input"}}
            MenuProps={{
              disableScrollLock: true,
            }}
          >
            {sortData?.map((sort) => (
              <MenuItem onKeyDown={(e: any) => handleKeyPress(e, 'Enter', () => handleSortChange)} key={sort.value} value={sort.value}>
                {sort.name || sort.label}
              </MenuItem>
            ))}
          </StyledSelect>
        </FormControl>
      )}

      {showView && (
            <ButtonGroup color='inherit'>
              {viewData.map((view) => (
                <StyledViewButton
                  key={view.value}
                  $active={view.value === viewValue}
                  onClick={() => handleViewChange(view.value)}
                  aria-label={locale?.viewMode + view.value.toString()}
                  $clicked={isClickedViewingButton}
                  disableElevation
                  id='btn-sortview-icon'
                >
                  {view.icon}
                </StyledViewButton>
              ))}
            </ButtonGroup>
      )}
    </StyledSortView>
  );
};

export default SortView;
